import { html, css } from 'lit'
import HHQuizElement from '../element.js'
import { repeat } from 'lit/directives/repeat.js'

/**
 * Quiz question input
 * - Radio if single option
 * - Checkbox if multiple options
 * @extends
 */
class HHQuizQuestionInput extends HHQuizElement {
  /**
   * Quiz question object
   */
  static properties = {
    question: { type: Object },
  }

  /**
   * Input type
   * @returns {String}
   *
   * - radio if single option
   * - checkbox if multiple options
   */
  get type() {
    return this.question.answer_format === 'multiple-options'
      ? 'checkbox'
      : 'radio'
  }

  /**
   * Respones for the current question
   */
  get currentResponses() {
    return this.store.getQuestionResponses(this.question.handle)
  }

  /**
   * Handles change event
   * @param {Event} ev
   * @param {Object} option
   * @returns {void}
   * @fires store#responses
   */
  handleChange(ev, option) {
    const wasPreviouslyCompleted = this.question.isComplete ? true : false
    this.store.responses.some((r) => {
      const isCurrentQuestion = r.question === this.question.handle
      if (isCurrentQuestion) {
        r.selection = !ev.target.checked
          ? r.selection.filter((o) => o !== option.handle)
          : r.selection.includes(option.handle)
          ? r.selection
          : this.type === 'checkbox'
          ? [...r.selection, option.handle]
          : [option.handle]
      }
      return isCurrentQuestion
    })

    this.store.responses = [...this.store.responses]

    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { option, hasChanged: false, wasPreviouslyCompleted },
        bubbles: true,
        composed: true,
      })
    )
  }

  /**
   * Handles click event
   * @param {Event} ev
   * @param {Object} option
   * @param {Boolean} isChecked
   * @fires change
   * @listens click on label
   */
  handleOptionClick(_, option, isChecked) {
    this.dispatchEvent(
      new CustomEvent('click-option', {
        detail: { option, hasChanged: !isChecked, wasPreviouslyCompleted: this.question.isComplete ? true : false },
        bubbles: true,
        composed: true,
      })
    )
  }

  /**
   * Renders the fieldset
   */
  render() {
    return html` <style>
        .hh-quiz-question-input {
          grid-template-columns: repeat(
            ${this.question.options.length > 4 ? 3 : 1},
            minmax(0, 1fr)
          );
        }
      </style>
      <fieldset class="hh-quiz-question-input">
        <legend class="visually-hidden">${this.question.question}</legend>

        ${repeat(
          this.question.options,
          (option) => `${this.question.handle}-${option.handle}`,
          (option) => {
            const isChecked = this.currentResponses.includes(option.handle)
            const focusFirst = isChecked || (!this.currentResponses.length && this.question.options[0] === option)
            const id = `${this.question.handle}-${option.handle}`

            return html`
              <div
                class="hh-quiz-question-input-option ${isChecked
                  ? 'is-checked'
                  : ''}"
              >
                <input
                  @change="${(ev) => this.handleChange(ev, option)}"
                  type="${this.type}"
                  id="${id}"
                  name="${this.type === 'checkbox'
                    ? option.handle
                    : this.question.handle}"
                  ?checked=${isChecked}
                  class="visually-hidden ${focusFirst ? 'focus-first' : ''}"
                />
                <label
                  @keydown="${(ev) => ev.stopPropagation()}"
                  @click="${(ev) => this.handleOptionClick(ev, option, isChecked)}"
                  for="${id}"
                >
                  ${option.image
                    ? html`<img
                        src="${option.image.src}"
                        alt="${option.image.alt}"
                        role="presentation"
                      />`
                    : ''}
                  <span>
                    <span>
                      <span class="h3">${option.name}</span>
                      ${option.description
                        ? html`<span class="caption"
                            >${option.description}</span
                          >`
                        : ''}
                    </span>
                  </span>
                </label>
              </div>
            `
          }
        )}
      </fieldset>`
    
  }

  static styles = [
    HHQuizElement.styles,
    css`
      .hh-quiz-question-input {
        display: grid !important;
        border: none;
        margin: 0 auto;
        gap: 20px;
        padding: 0;
      }
      @media screen and (max-width: ${HHQuizElement.mq.sm}px) {
        .hh-quiz-question-input {
          grid-template-columns: repeat(1, minmax(0, 1fr));
        }
      }
      .hh-quiz-question-input-option {
        display: block;
        width: 100%;
        position: relative;
        max-width: max(21vw, 315px);
        margin: 0 auto;
      }
      .hh-quiz-question-input-option input {
        z-index: -1;
        opacity: 0;
        position: absolute;
      }
      .hh-quiz-question-input-option label {
        border: 1px solid var(--hh-color-grey-mineral);
        position: relative;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: stretch;
        justify-content: start;
        cursor: pointer;
        min-height: 60px;
        display: flex;
        box-sizing: border-box;
      }
      .hh-quiz-question-input-option.is-checked label {
        border-color: var(--hh-color-honey-two);
        outline: 1px solid var(--hh-color-honey-two);
      }
      .hh-quiz-question-input-option input:focus-visible + label {
        outline: 0.2rem solid var(--hh-color-focus);
        outline-offset: var(--hh-focus-outline-offset);
      }
      .hh-quiz-question-input-option label img {
        width: max(5vw, 78px);
        height: 100%;
        min-height: 78px;
        display: block;
        flex-shrink: 0;
        object-fit: cover;
        background: var(--hh-color-grey-mineral);
      }
      .hh-quiz-question-input-option label > span {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-grow: 1;
      }
      .hh-quiz-question-input-option label .h3 {
        color: var(--hh-color-aged-black);
        display: block;
        text-decoration: none !important;
      }
      .hh-quiz-question-input-option label .caption {
        color: var(--hh-color-ancient-ore);
        display: block;
      }
      .hh-quiz-question-input-option label > span {
        padding: 0.5em;
      }
      .hh-quiz-question-input-option label img + span {
        justify-content: start;
        padding: 15px 20px;
        text-align: left;
      }
    `,
  ]
}

customElements.define('hh-quiz-question-input', HHQuizQuestionInput)
